import React from 'react';
import { EyeInvisibleOutlined, GlobalOutlined, SettingOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import styled from 'styled-components';

import { useLoyaltyProgram } from 'api/loyalty';
import { useTypedSelector } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { DEFAULT_TIMEZONE, zoneOffset } from '../configStudio/configModel';
import { ConfigStepPanel, TC } from '../configStudio/kit/ConfigPanel';
import { CardChoice } from '../configStudio/kit/OptionTile';
import { DayTimeFields, LimitLine } from '../configStudio/kit/configFields';
import { Checkbox, RadioGroup } from '../wizard/kit/controls';
import { DateField, LoyaltySelect, SelectField, TextField } from '../wizard/kit/fields';
import { Divider, Form, Hint, Line, Words } from '../wizard/kit/layout';
import { wz, wzType } from '../wizard/wizardStyles';
import { rewardStepErrors } from './rewardAdapter';
import {
  Expiry,
  IRewardStepProps,
  REWARD_STEP_TOTAL,
  RewardLimits,
  RewardWizardState,
  Visibility
} from './rewardModel';

const TR = `${TC}.reward`;

const useTimezone = () =>
  useTypedSelector(store => store.global?.tenantConfig?.localization?.timezone ?? DEFAULT_TIMEZONE);

const SubQuestion = styled.h3`
  margin: 18px 0 8px;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.question}
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 11px;
`;

const VISIBILITY: { value: Visibility; icon: React.ReactNode }[] = [
  { value: 'public', icon: <GlobalOutlined /> },
  { value: 'private', icon: <EyeInvisibleOutlined /> },
  { value: 'system', icon: <SettingOutlined /> }
];

type Patch = (patch: Partial<RewardWizardState>) => void;

const usePatch =
  (update: IRewardStepProps['update']): Patch =>
  change =>
    update(current => ({ ...current, ...change }));

/** 62 Step 3/5: Public / Private / System, then the selling period and its days. */
export const RewardVisibilityStep: React.FC<IRewardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const timezone = useTimezone();
  const patch = usePatch(update);
  const TV = `${TR}.visibility`;
  const zone = t(`${TC}.timezone`, { offset: zoneOffset(timezone) });
  return (
    <ConfigStepPanel
      title={t(`${TR}.title`)}
      step={3}
      total={REWARD_STEP_TOTAL}
      question={t(`${TV}.question`)}
      errors={rewardStepErrors(state, t)}
      onNext={next}
      onBack={back}
      testId="orbit-reward-step-visibility"
    >
      <Cards role="radiogroup" aria-label={t(`${TV}.question`)}>
        {VISIBILITY.map(option => (
          <CardChoice
            key={option.value}
            icon={option.icon}
            title={t(`${TV}.${option.value}`)}
            hint={t(`${TV}.${option.value}Hint`)}
            selected={state.visibility === option.value}
            onSelect={() => patch({ visibility: option.value })}
          />
        ))}
      </Cards>
      <SubQuestion>{t(`${TV}.availability`)}</SubQuestion>
      <Form>
        <Line>
          <Words>{t(`${TV}.start`)}</Words>
          <DateField
            label={t(`${TV}.start`)}
            withTime
            value={state.beginsAt}
            onChange={beginsAt => patch({ beginsAt })}
          />
          <Hint>{zone}</Hint>
          <Checkbox
            checked={state.endsOn}
            label={t(`${TV}.hasEnd`)}
            showLabel
            onChange={endsOn => patch({ endsOn })}
          />
        </Line>
        {state.endsOn ? (
          <Line>
            <Words>{t(`${TV}.end`)}</Words>
            <DateField
              label={t(`${TV}.end`)}
              withTime
              value={state.endsAt}
              onChange={endsAt => patch({ endsAt })}
            />
            <Hint>{zone}</Hint>
          </Line>
        ) : null}
        <DayTimeFields
          label={t(`${TV}.certainDays`)}
          value={state.window}
          onChange={window => patch({ window })}
          zoneLabel={zone}
          rangeLabels="startEnd"
        />
      </Form>
    </ConfigStepPanel>
  );
};

/** 63 Step 4/5: never, on a date, or a number of days after issuance. */
export const RewardExpiryStep: React.FC<IRewardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const timezone = useTimezone();
  const patch = usePatch(update);
  const TX = `${TR}.expiry`;
  const choose = (expiry: Expiry) => patch({ expiry });
  return (
    <ConfigStepPanel
      title={t(`${TR}.title`)}
      step={4}
      total={REWARD_STEP_TOTAL}
      question={t(`${TX}.question`)}
      errors={rewardStepErrors(state, t)}
      onNext={next}
      onBack={back}
      testId="orbit-reward-step-expiry"
    >
      <Form>
        <Line>
          <RadioGroup<Expiry>
            name="orbit-reward-expiry-never"
            value={state.expiry}
            options={[{ value: 'never', label: t(`${TX}.never`) }]}
            onChange={choose}
          />
          <Hint>{t(`${TX}.neverHint`)}</Hint>
        </Line>
        <Line>
          <RadioGroup<Expiry>
            name="orbit-reward-expiry-fixed"
            value={state.expiry}
            options={[{ value: 'fixed', label: t(`${TX}.fixed`) }]}
            onChange={choose}
          />
          <DateField
            label={t(`${TX}.fixedPlaceholder`)}
            withTime
            value={state.expiresAt}
            disabled={state.expiry !== 'fixed'}
            onChange={expiresAt => patch({ expiresAt })}
          />
          <Hint>{t(`${TC}.timezone`, { offset: zoneOffset(timezone) })}</Hint>
        </Line>
        <Line>
          <RadioGroup<Expiry>
            name="orbit-reward-expiry-days"
            value={state.expiry}
            options={[{ value: 'days', label: t(`${TX}.days`) }]}
            onChange={choose}
          />
          <TextField
            label={t(`${TX}.days`)}
            placeholder={t(`${TX}.daysPlaceholder`)}
            inputMode="numeric"
            value={state.expiryDays}
            disabled={state.expiry !== 'days'}
            onChange={expiryDays => patch({ expiryDays })}
          />
          <Words $muted>{t(`${TX}.daysUnit`)}</Words>
        </Line>
      </Form>
    </ConfigStepPanel>
  );
};

// The module's cost currencies are free text in `RewardInfo`; PHP is the one the frame draws.
const CURRENCIES = ['PHP', 'SGD', 'MYR', 'IDR', 'THB', 'USD'];

const PriceRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 7px 0;

  > :first-child {
    flex: 0 0 204px;
    padding-top: 9px;
  }
`;

// 6985:8478: "Reward Cost price" / "Reward selling price" in Bold 13, the primary ink.
const PriceLabel = styled.span`
  color: ${wz.text};
  font-weight: 700;
  ${wzType.label}
`;

const Labelled = styled.label`
  display: flex;
  flex-direction: column;

  > span {
    margin-bottom: 6px;
    color: ${wz.muted};
    ${wzType.hint}
  }
`;

const TierSelect: React.FC<{ state: RewardWizardState; patch: Patch }> = ({ state, patch }) => {
  const { t } = useMultiLanguageTranslation();
  const { data, isLoading } = useLoyaltyProgram(state.sellLoyalty?.id ?? null);
  return (
    <Select<string[]>
      mode="multiple"
      aria-label={t(`${TR}.limits.tiers`)}
      style={{ width: 300 }}
      disabled={!state.sellOn}
      loading={isLoading}
      value={state.sellTiers}
      options={(data?.tiers ?? []).map(tier => ({ value: tier.name, label: tier.name }))}
      onChange={sellTiers => patch({ sellTiers })}
    />
  );
};

/** 64 Step 5/5: four limits, cost price and selling price; "Finalise" goes to the review. */
export const RewardLimitsStep: React.FC<IRewardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const patch = usePatch(update);
  const TL = `${TR}.limits`;
  const { limits } = state;
  const setLimits = (change: Partial<RewardLimits>) =>
    update(current => ({ ...current, limits: { ...current.limits, ...change } }));
  return (
    <ConfigStepPanel
      title={t(`${TR}.title`)}
      step={5}
      total={REWARD_STEP_TOTAL}
      question={t(`${TL}.question`)}
      errors={rewardStepErrors(state, t)}
      onNext={next}
      onBack={back}
      last
      testId="orbit-reward-step-limits"
    >
      <Form>
        <LimitLine
          label={t(`${TL}.issuance`)}
          unit={t(`${TL}.per`)}
          on={limits.issuanceOn}
          value={limits.issuance}
          period={limits.issuancePeriod}
          periodCase="title"
          spread
          onToggle={issuanceOn => setLimits({ issuanceOn })}
          onValue={issuance => setLimits({ issuance })}
          onPeriod={issuancePeriod => setLimits({ issuancePeriod })}
        />
        <LimitLine
          label={t(`${TL}.lifetime`)}
          unit={t(`${TL}.lifetimeUnit`)}
          spread
          on={limits.lifetimeOn}
          value={limits.lifetime}
          onToggle={lifetimeOn => setLimits({ lifetimeOn })}
          onValue={lifetime => setLimits({ lifetime })}
        />
        <LimitLine
          label={t(`${TL}.perUser`)}
          unit={t(`${TL}.per`)}
          on={limits.perUserOn}
          value={limits.perUser}
          period={limits.perUserPeriod}
          periodCase="title"
          spread
          onToggle={perUserOn => setLimits({ perUserOn })}
          onValue={perUser => setLimits({ perUser })}
          onPeriod={perUserPeriod => setLimits({ perUserPeriod })}
        />
        <LimitLine
          label={t(`${TL}.redemption`)}
          unit={t(`${TL}.per`)}
          on={limits.redemptionOn}
          value={limits.redemption}
          period={limits.redemptionPeriod}
          periodCase="title"
          spread
          onToggle={redemptionOn => setLimits({ redemptionOn })}
          onValue={redemption => setLimits({ redemption })}
          onPeriod={redemptionPeriod => setLimits({ redemptionPeriod })}
        />
        <Divider />
        <PriceRow>
          <PriceLabel>{t(`${TL}.cost`)}</PriceLabel>
          <Line style={{ padding: 0 }}>
            <SelectField<string>
              label={t(`${TL}.currency`)}
              value={state.costCurrency}
              options={CURRENCIES.map(code => ({ value: code, label: code }))}
              onChange={costCurrency => patch({ costCurrency })}
            />
            <TextField
              label={t(`${TL}.cost`)}
              placeholder="0.00"
              inputMode="decimal"
              value={state.cost}
              onChange={cost => patch({ cost })}
            />
          </Line>
        </PriceRow>
        <PriceRow>
          <PriceLabel>{t(`${TL}.selling`)}</PriceLabel>
          <div>
            <Line style={{ paddingTop: 0 }}>
              <Checkbox
                checked={state.sellOn}
                label={t(`${TL}.sell`)}
                onChange={sellOn => patch({ sellOn })}
              />
              <Words $muted={!state.sellOn}>{t(`${TL}.sell`)}</Words>
              <TextField
                label={t(`${TL}.sell`)}
                placeholder="0"
                inputMode="numeric"
                value={state.sellPoints}
                disabled={!state.sellOn}
                onChange={sellPoints => patch({ sellPoints })}
              />
              <Words $muted>{t(`${TL}.points`)}</Words>
            </Line>
            <Line style={{ opacity: state.sellOn ? 1 : 0.45 }}>
              <Labelled>
                <span>{t(`${TL}.loyalty`)}</span>
                <LoyaltySelect
                  value={state.sellLoyalty}
                  onChange={sellLoyalty => patch({ sellLoyalty, sellTiers: [] })}
                />
              </Labelled>
              <Labelled>
                <span>{t(`${TL}.tiers`)}</span>
                <TierSelect state={state} patch={patch} />
              </Labelled>
            </Line>
          </div>
        </PriceRow>
      </Form>
    </ConfigStepPanel>
  );
};
